'use client';
import { useEffect, useRef, useState } from 'react';
import { trackEvent } from '@/lib/analytics/counterscale';

/* Shared share behavior for every daily game (SPOT, Connections, Decode,
   Grid, Duel). The visual button stays per-game; this hook is the common
   baseline required by docs/daily-game-baseline-requirements.md §5:
   - fires the `<game>_share` Counterscale event on a successful share/copy
     (a canceled OS share sheet does NOT count)
   - uses the Web Share API on touch devices (opens straight into
     Messages/WhatsApp/etc.), clipboard copy elsewhere
   - `copied` drives the button's "Copied!" flash for the clipboard path */
export function useShareResult(game: string, text: string) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  async function share() {
    const useShareSheet =
      typeof navigator.share === 'function' &&
      window.matchMedia('(pointer: coarse)').matches;
    if (useShareSheet) {
      try {
        await navigator.share({ text });
        trackEvent(`${game}_share`);
        return;
      } catch (err) {
        // User dismissed the share sheet — not a share, not an error.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Anything else (e.g. data not shareable): fall through to clipboard.
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      trackEvent(`${game}_share`);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  }

  return { share, copied };
}
